import { FieldErrors, useForm } from 'react-hook-form';
import { useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '@ramsey-design-system/form-field';
import { useGAFormTracking } from '../../../../../shared/hooks';
import { useReferralContext } from '../../../context';
import { phoneSchema } from '../../../schemas/phoneSchema';
import { useWizard } from 'react-use-wizard';
import { startVerification } from '../../../api';
import Button from '@ramsey-design-system/button';
import { ChatIcon, PhoneIcon } from '@ramsey-design-system/icons';
import { PatternFormat } from 'react-number-format';
import Input from '@ramsey-design-system/input';
import { trackStepCompleted } from '../../../../../shared/analytics';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../../utils';
import {
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../../stepComponents';
import { PhoneVerificationCountDownAlert } from '../../PhoneVerificationCountDownAlert';
import { ErrorMessage } from '../../messages';
import { RamseyAgreementText } from '../../agreements';

const STEP_NAME = 'phone_step';
const STEP_LABEL = "What's your phone number?";
const SEND_FAILURE_MESSAGE =
  'Sorry, we couldn’t send a verification code. Try\u00A0again.';

interface PhoneStepFields {
  phone: string;
}

function PhoneStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const { nextStep, activeStep } = useWizard();
  const [errorMessage, setErrorMessage] = useState('');
  const verificationType = useRef<'call' | 'sms' | ''>('');
  const [isLoadingSms, setIsLoadingSms] = useState(false);
  const [isLoadingCall, setIsLoadingCall] = useState(false);
  // Set synchronously, unlike the isLoading flags, which only land after react-hook-form's async
  // validation resolves and a re-render - the buttons are still enabled in that gap.
  const requestInFlight = useRef(false);
  const {
    referral,
    updateReferral,
    phoneVerificationCountDown,
    updatePhoneVerificationCountDown
  } = useReferralContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm<PhoneStepFields>({
    defaultValues: {
      ...referral
    },
    resolver: yupResolver(phoneSchema)
  });

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(values: PhoneStepFields) {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    try {
      const tempVerificationType =
        verificationType.current === 'call' ? 'call' : 'sms';
      setErrorMessage('');
      const isLoading =
        tempVerificationType === 'sms' ? setIsLoadingSms : setIsLoadingCall;
      isLoading(true);
      const phone = values.phone.replaceAll(/\D/g, '');
      updateReferral({
        phone,
        verification_type: tempVerificationType
      });
      await startVerification(
        phone,
        tempVerificationType,
        referral.postal_code
      );
      trackStepCompleted({
        step: activeStep + 1,
        step_name: STEP_NAME,
        step_label: STEP_LABEL,
        answer_labels: ['Phone'],
        answer_values: [phone],
        form_name: getFormName()
      });
      updatePhoneVerificationCountDown(30);
      await nextStep();
    } catch (e) {
      setErrorMessage(SEND_FAILURE_MESSAGE);
      trackError('system', SEND_FAILURE_MESSAGE);
      console.error(e);
    } finally {
      verificationType.current = '';
      requestInFlight.current = false;
      setIsLoadingCall(false);
      setIsLoadingSms(false);
    }
  }

  function onInvalid(formErrors: FieldErrors<PhoneStepFields>) {
    trackError('validation', formErrors.phone?.message, 'phone');
  }

  function setCallVerification() {
    verificationType.current = 'call';
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  function setTextingVerification() {
    verificationType.current = 'sms';
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  return (
    <StepWrapper>
      {/* eslint-disable-next-line react-hooks/refs */}
      <form ref={formRef} onSubmit={handleSubmit(onValidSubmit, onInvalid)}>
        <StandardStepTitle>What's your phone number?</StandardStepTitle>
        <StandardStepSubtitleText>
          To make sure we’ve got the right number, we’ll send you a six
          {'\u2011'}digit
          {'\u00A0'}code.
        </StandardStepSubtitleText>
        <div className="space-y-2 mb-4">
          <FormField
            id="phone"
            label="Phone"
            size="lg"
            invalid={!!errors.phone}
            errors={[errors.phone?.message]}
          >
            {() => (
              <PatternFormat
                {...register('phone')}
                type="tel"
                id="phone"
                autoComplete="tel-national"
                defaultValue={referral?.phone}
                size="lg"
                invalid={!!errors.phone}
                data-testid="tax_phone_input"
                placeholder="(___) ___-____"
                format="(###) ###-####"
                mask="_"
                onValueChange={(value) => {
                  setValue('phone', value.formattedValue);
                }}
                customInput={Input}
              />
            )}
          </FormField>
        </div>

        {phoneVerificationCountDown > 0 && (
          <PhoneVerificationCountDownAlert
            phoneVerificationCountDown={phoneVerificationCountDown}
          />
        )}

        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        <div className="flex flex-col gap-1 items-center mb-4">
          <Button
            type="submit"
            icon={ChatIcon}
            className="w-full!"
            fullWidthOnMobile={true}
            data-testid="tax_text_me_button"
            size="large"
            aria-label="Send verification code via text"
            loading={isLoadingSms}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setTextingVerification();
              }
            }}
            onClick={() => {
              setTextingVerification();
            }}
            disabled={
              phoneVerificationCountDown > 0 || isLoadingSms || isLoadingCall
            }
          >
            {isLoadingSms ? 'Texting...' : 'Text Me'}
          </Button>
          <Button
            type="submit"
            icon={PhoneIcon}
            className="w-full!"
            appearance="subtle"
            fullWidthOnMobile={true}
            data-testid="tax_call_me_button"
            size="large"
            aria-label="Send verification code via phone call"
            loading={isLoadingCall}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setCallVerification();
              }
            }}
            onClick={() => {
              setCallVerification();
            }}
            disabled={
              phoneVerificationCountDown > 0 || isLoadingSms || isLoadingCall
            }
          >
            {isLoadingCall ? 'Calling...' : 'Call Me'}
          </Button>
        </div>
        <RamseyAgreementText />
      </form>
    </StepWrapper>
  );
}

export { PhoneStep };
